export type ReceiptData = {
  receiptNo: string;
  employeeName: string;
  employeeCode?: string;
  department?: string;
  amount: number;
  periodLabel: string;
  workedDays: number;
  approvedAt: string | Date;
  approverName: string;
  reason?: string;
};

export function thaiBahtText(num: number): string {
  if (!num || isNaN(num) || num <= 0) return "ศูนย์บาทถ้วน";
  const numStr = ["ศูนย์", "หนึ่ง", "สอง", "สาม", "สี่", "ห้า", "หก", "เจ็ด", "แปด", "เก้า"];
  const unitStr = ["", "สิบ", "ร้อย", "พัน", "หมื่น", "แสน", "ล้าน"];

  const [intPart, decPart] = Number(num).toFixed(2).split(".");

  const convertGroup = (digits: string) => {
    let s = "";
    const len = digits.length;
    for (let i = 0; i < len; i++) {
      const digit = Number(digits[i]);
      const unit = len - i - 1;
      if (digit !== 0) {
        if (unit === 1 && digit === 1) {
          s += "สิบ";
        } else if (unit === 1 && digit === 2) {
          s += "ยี่สิบ";
        } else if (unit === 0 && digit === 1 && len > 1 && digits[len - 2] !== "0") {
          s += "เอ็ด";
        } else {
          s += numStr[digit] + unitStr[unit];
        }
      }
    }
    return s;
  };

  let result = "";
  const intVal = parseInt(intPart, 10);
  if (intVal >= 1000000) {
    const millions = Math.floor(intVal / 1000000);
    const remainder = intVal % 1000000;
    result += convertGroup(String(millions)) + "ล้าน";
    if (remainder > 0) {
      result += convertGroup(String(remainder));
    }
  } else {
    result += convertGroup(String(intVal));
  }

  result += "บาท";

  const decVal = parseInt(decPart, 10);
  if (decVal === 0) {
    result += "ถ้วน";
  } else {
    result += convertGroup(decPart) + "สตางค์";
  }
  return result;
}

export function openReceiptPdfWindow(data: ReceiptData) {
  const printWindow = window.open("", "_blank");
  if (!printWindow) {
    alert("กรุณาอนุญาต Pop-up บนเบราว์เซอร์เพื่อเปิดใบเสร็จ A4");
    return;
  }

  const approvedDateStr = new Date(data.approvedAt).toLocaleDateString("th-TH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const approvedTimeStr = new Date(data.approvedAt).toLocaleTimeString("th-TH", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const printedDateStr = new Date().toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const bahtText = thaiBahtText(data.amount);

  const html = `<!DOCTYPE html>
<html lang="th">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>ใบเสร็จรับเงิน - ${data.receiptNo}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 15mm 12mm 15mm;
    }
    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      font-family: 'Sarabun', 'TH Sarabun New', -apple-system, BlinkMacSystemFont, 'Segoe UI', Tahoma, sans-serif;
      margin: 0;
      padding: 20px;
      color: #0f172a;
      background: #f1f5f9;
      font-size: 13.5px;
      line-height: 1.5;
    }
    .toolbar {
      max-width: 180mm;
      margin: 0 auto 16px auto;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #ffffff;
      padding: 10px 16px;
      border-radius: 12px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 2px 8px rgba(0,0,0,0.06);
    }
    .toolbar-title {
      font-weight: 700;
      font-size: 14px;
      color: #0f766e;
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 18px;
      font-size: 13px;
      font-weight: 700;
      font-family: inherit;
      border-radius: 8px;
      cursor: pointer;
      border: 0;
      transition: all 0.15s ease;
    }
    .btn-primary {
      background: #0f766e;
      color: #ffffff;
    }
    .btn-primary:hover {
      background: #0d655f;
    }
    .btn-secondary {
      background: #e2e8f0;
      color: #334155;
      margin-left: 8px;
    }
    .btn-secondary:hover {
      background: #cbd5e1;
    }
    .a4-sheet {
      width: 180mm;
      min-height: 250mm;
      max-width: 100%;
      margin: 0 auto;
      background: #ffffff;
      padding: 24mm 20mm;
      border-radius: 8px;
      border: 1px solid #cbd5e1;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2.5px solid #0f766e;
      padding-bottom: 14px;
      margin-bottom: 20px;
    }
    .org-title {
      font-size: 22px;
      font-weight: 800;
      color: #0f766e;
      letter-spacing: -0.3px;
      margin: 0 0 4px 0;
    }
    .org-sub {
      font-size: 12px;
      color: #64748b;
      font-weight: 500;
    }
    .doc-meta {
      text-align: right;
    }
    .doc-title {
      font-size: 19px;
      font-weight: 800;
      color: #0f172a;
      margin: 0 0 4px 0;
    }
    .doc-no {
      font-size: 13px;
      color: #475569;
      font-weight: 600;
    }
    .doc-badge {
      display: inline-block;
      margin-top: 4px;
      padding: 2px 8px;
      font-size: 11px;
      font-weight: 700;
      color: #065f46;
      background: #d1fae5;
      border: 1px solid #a7f3d0;
      border-radius: 6px;
    }
    .info-card {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px 20px;
      background: #f8fafc;
      padding: 14px 18px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      margin-bottom: 20px;
    }
    .info-row {
      display: flex;
      font-size: 13px;
    }
    .info-label {
      color: #64748b;
      font-weight: 600;
      width: 130px;
      shrink: 0;
    }
    .info-val {
      color: #0f172a;
      font-weight: 700;
    }
    table.data-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    table.data-table th {
      background: #f1f5f9;
      color: #1e293b;
      font-weight: 700;
      font-size: 12.5px;
      padding: 9px 12px;
      border-top: 1px solid #cbd5e1;
      border-bottom: 2px solid #94a3b8;
      text-align: left;
    }
    table.data-table td {
      padding: 11px 12px;
      border-bottom: 1px solid #e2e8f0;
      font-size: 13px;
      vertical-align: middle;
    }
    .total-container {
      background: #ecfdf5;
      border: 1.5px solid #10b981;
      border-radius: 8px;
      padding: 12px 18px;
      margin-bottom: 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .total-text-side .total-label {
      font-size: 14px;
      font-weight: 700;
      color: #065f46;
      margin-bottom: 2px;
    }
    .total-text-side .baht-text {
      font-size: 12.5px;
      color: #047857;
      font-weight: 600;
    }
    .total-amount {
      font-size: 24px;
      font-weight: 800;
      color: #047857;
      letter-spacing: -0.3px;
    }
    .note-box {
      font-size: 11.5px;
      color: #64748b;
      background: #f8fafc;
      border-left: 3px solid #0f766e;
      padding: 8px 12px;
      margin-bottom: 28px;
      border-radius: 0 6px 6px 0;
    }
    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 40px;
      margin-top: 15px;
      padding-top: 16px;
      border-top: 1px dashed #cbd5e1;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .sig-col {
      text-align: center;
    }
    .sig-space {
      height: 48px;
      border-bottom: 1px dotted #94a3b8;
      margin-bottom: 8px;
    }
    .sig-name {
      font-weight: 700;
      font-size: 12.5px;
      color: #0f172a;
    }
    .sig-role {
      font-size: 11.5px;
      color: #64748b;
      margin-top: 2px;
    }
    .sig-date {
      font-size: 11px;
      color: #94a3b8;
      margin-top: 4px;
    }
    .footer-note {
      text-align: center;
      font-size: 10.5px;
      color: #94a3b8;
      margin-top: 28px;
      padding-top: 8px;
      border-top: 1px solid #f1f5f9;
    }
    @media print {
      body {
        background: #ffffff !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .toolbar {
        display: none !important;
      }
      .a4-sheet {
        width: 100% !important;
        min-height: auto !important;
        margin: 0 !important;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="toolbar-title">
      📄 ตัวอย่างเอกสารขนาด A4 (Portrait)
    </div>
    <div>
      <button class="btn btn-primary" onclick="window.print()">🖨️ พิมพ์เอกสาร / บันทึก PDF</button>
      <button class="btn btn-secondary" onclick="window.close()">✕ ปิด</button>
    </div>
  </div>

  <div class="a4-sheet">
    <div>
      <div class="header">
        <div>
          <h1 class="org-title">DEMO CHECK-IN</h1>
          <div class="org-sub">ระบบบริหารจัดการลงเวลาและบุคลากรอิเล็กทรอนิกส์</div>
        </div>
        <div class="doc-meta">
          <div class="doc-title">ใบสำคัญจ่าย / ใบเสร็จรับเงิน</div>
          <div class="doc-no">เลขที่: <strong>${data.receiptNo}</strong></div>
          <div class="doc-badge">อนุมัติเรียบร้อยแล้ว</div>
        </div>
      </div>

      <div class="info-card">
        <div class="info-row">
          <span class="info-label">ผู้รับเงิน (พนักงาน):</span>
          <span class="info-val">${data.employeeName}</span>
        </div>
        <div class="info-row">
          <span class="info-label">วันที่อนุมัติจ่าย:</span>
          <span class="info-val">${approvedDateStr} (${approvedTimeStr} น.)</span>
        </div>
        <div class="info-row">
          <span class="info-label">งวดการคำนวณ:</span>
          <span class="info-val">${data.periodLabel}</span>
        </div>
        <div class="info-row">
          <span class="info-label">ผู้อนุมัติการจ่าย:</span>
          <span class="info-val">${data.approverName}</span>
        </div>
        ${data.employeeCode ? `<div class="info-row"><span class="info-label">รหัสพนักงาน:</span><span class="info-val">${data.employeeCode}</span></div>` : ''}
        ${data.department ? `<div class="info-row"><span class="info-label">แผนก / ฝ่าย:</span><span class="info-val">${data.department}</span></div>` : ''}
      </div>

      <table class="data-table">
        <thead>
          <tr>
            <th style="width: 45px; text-align: center;">ลำดับ</th>
            <th>รายการ / รายละเอียดการเบิกเงิน</th>
            <th style="width: 130px; text-align: center;">วันทำงานสะสมในงวด</th>
            <th style="width: 140px; text-align: right;">จำนวนเงิน (บาท)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="text-align: center;">1</td>
            <td>
              <strong>เงินเดือนเบิกล่วงหน้าระหว่างงวด (Salary Advance)</strong>
              <div style="font-size: 11.5px; color: #64748b; margin-top: 2px;">
                วัตถุประสงค์: ${data.reason || "ขอเบิกตามสิทธิ์ทำงานจริงในงวดปัจจุบัน"}
              </div>
            </td>
            <td style="text-align: center; font-weight: 600;">${data.workedDays} วัน</td>
            <td style="text-align: right; font-weight: 700; font-size: 14px;">฿${data.amount.toLocaleString("th-TH", { minimumFractionDigits: 2 })}</td>
          </tr>
        </tbody>
      </table>

      <div class="total-container">
        <div class="total-text-side">
          <div class="total-label">รวมยอดเงินจ่ายสุทธิ (Total Amount Paid)</div>
          <div class="baht-text">(${bahtText})</div>
        </div>
        <div class="total-amount">฿${data.amount.toLocaleString("th-TH", { minimumFractionDigits: 2 })}</div>
      </div>

      <div class="note-box">
        <strong>ข้อกำหนดและเงื่อนไข:</strong> ยอดเงินที่ได้รับอนุมัติในเอกสารนี้ จะถูกนำไปหักลบออกจากเงินเดือนสุทธิประจำงวดการทำงาน ${data.periodLabel} โดยอัตโนมัติ ตามระเบียบข้อบังคับขององค์กร
      </div>
    </div>

    <div>
      <div class="signatures">
        <div class="sig-col">
          <div class="sig-space"></div>
          <div class="sig-name">ลงชื่อ: ${data.employeeName}</div>
          <div class="sig-role">ผู้รับเงิน / พนักงาน</div>
          <div class="sig-date">วันที่ ......./......./...........</div>
        </div>
        <div class="sig-col">
          <div class="sig-space"></div>
          <div class="sig-name">ลงชื่อ: ${data.approverName}</div>
          <div class="sig-role">ผู้อนุมัติจ่าย / ตัวแทนฝ่ายบริหาร</div>
          <div class="sig-date">วันที่ ......./......./...........</div>
        </div>
      </div>

      <div class="footer-note">
        เอกสารนี้สร้างขึ้นโดยระบบอัตโนมัติ Demo Check-In · ออกเอกสารเมื่อ ${printedDateStr} · จัดรูปแบบกระดาษมาตรฐาน A4
      </div>
    </div>
  </div>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
}
